import { useId } from 'react';
import styles from './BrandLogo.module.css';

/**
 * The TOTTALO logotype, drawn rather than set.
 *
 * Heavy geometric letterforms — cap height 100, stem 25, circular O, 11-unit
 * tracking — built from primitives and emitted as one evenodd path, so the
 * counters are holes and the mark keeps its colour from the palette's metallic
 * ramp (--theme-brand-ramp-*) instead of a flat fill. Setting the name in a
 * webfont left it at the mercy of whatever loaded; this is stable at every size
 * and in every theme.
 *
 * Geometry source: the generator that produced this path is documented in the
 * commit that introduced it; edit the numbers here, they are the artwork now.
 */
export const WORDMARK_VIEWBOX = '-6 -6 680 112';

/**
 * One entry per glyph, T-O-T-T-A-L-O, each holding that letter's subpaths
 * (counters included, so fill them evenodd). The page loader animates them
 * one by one; the logotype joins them back into a single path.
 */
export const WORDMARK_LETTERS: readonly string[] = [
  'M0.0,0 H80.0 V25.0 H52.5 V100.0 H27.5 V25.0 H0.0 Z',
  'M91.0,50.0 a50.0,50.0 0 1,0 100.0,0 a50.0,50.0 0 1,0 -100.0,0 Z M116.0,50.0 a25.0,25.0 0 1,0 50.0,0 a25.0,25.0 0 1,0 -50.0,0 Z',
  'M202.0,0 H282.0 V25.0 H254.5 V100.0 H229.5 V25.0 H202.0 Z',
  'M293.0,0 H373.0 V25.0 H345.5 V100.0 H320.5 V25.0 H293.0 Z',
  'M384.0,100.0 L419.0,0 H441.0 L476.0,100.0 H451.0 L409.0,100.0 Z M430.0,40.0 L437.7,62.0 H422.3 Z M416.7,78.0 H443.3 L451.0,100.0 H409.0 Z',
  'M487.0,0 H512.0 V75.0 H557.0 V100.0 H487.0 Z',
  'M568.0,50.0 a50.0,50.0 0 1,0 100.0,0 a50.0,50.0 0 1,0 -100.0,0 Z M593.0,50.0 a25.0,25.0 0 1,0 50.0,0 a25.0,25.0 0 1,0 -50.0,0 Z',
];

const MARK = WORDMARK_LETTERS.join(' ');

type WordmarkProps = {
  className?: string;
  title?: string;
};

export function Wordmark({ className, title = 'Tottalo' }: WordmarkProps) {
  const rampId = `${useId()}-brand`;

  return (
    <svg
      className={className}
      viewBox={WORDMARK_VIEWBOX}
      role="img"
      aria-label={title}
      preserveAspectRatio="xMinYMid meet"
    >
      <defs>
        <linearGradient id={rampId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--theme-brand-ramp-1)" />
          <stop offset="52%" stopColor="var(--theme-brand-ramp-2)" />
          <stop offset="100%" stopColor="var(--theme-brand-ramp-3)" />
        </linearGradient>
      </defs>
      <path
        className={styles.markPath}
        fill={`url(#${rampId})`}
        fillRule="evenodd"
        d={MARK}
      />
    </svg>
  );
}
